import React from 'react';
import { AlertTriangle, RefreshCw, Home, Mail, Clock, Wifi } from 'lucide-react';
import ApiService from '../services/ApiService';

class AuthErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isRetrying: false,
      retryCount: 0,
      errorType: 'unknown'
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const errorType = this.categorizeError(error);

    this.setState({
      error: error,
      errorInfo: errorInfo,
      errorType: errorType
    });

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Authentication Error:', error, errorInfo);
    }

    // In production, you might want to send this to an error tracking service
    // Example: Sentry, LogRocket, etc.
    // this.reportError(error, errorInfo);

    // Log to backend
    ApiService.logError(
      `AuthErrorBoundary caught: ${error.message}\nInfo: ${JSON.stringify(errorInfo)}`,
      "AuthErrorBoundary",
      "High"
    );
  }

  categorizeError = (error) => {
    const message = error?.message?.toLowerCase() || '';

    if (message.includes('network') || message.includes('fetch')) {
      return 'network';
    } else if (message.includes('clerk') || message.includes('auth')) {
      return 'auth';
    } else if (message.includes('chunk') || message.includes('loading')) {
      return 'loading';
    }

    return 'unknown';
  };

  getErrorContent = () => {
    const { errorType } = this.state;

    switch (errorType) {
      case 'network':
        return {
          icon: <Wifi className="w-8 h-8 text-black" />,
          title: 'Network Connection Issue',
          description: 'Unable to connect to our servers. Please check your internet connection and try again.',
          bgColor: 'bg-gray-100',
          suggestion: 'Check your internet connection'
        };
      case 'auth':
        return {
          icon: <AlertTriangle className="w-8 h-8 text-black" />,
          title: 'Authentication Error',
          description: 'We encountered an issue with the authentication system. This might be due to a temporary service disruption.',
          bgColor: 'bg-gray-100',
          suggestion: 'Try refreshing the page'
        };
      case 'loading':
        return {
          icon: <Clock className="w-8 h-8 text-black" />,
          title: 'Loading Error',
          description: 'Some application resources failed to load. This might be due to a slow connection or server issue.',
          bgColor: 'bg-gray-100',
          suggestion: 'Refresh to reload resources'
        };
      default:
        return {
          icon: <AlertTriangle className="w-8 h-8 text-black" />,
          title: 'Something went wrong',
          description: 'An unexpected error occurred. Our team has been notified and is working on a fix.',
          bgColor: 'bg-gray-100',
          suggestion: 'Try again in a few moments'
        };
    }
  };

  handleRetry = async () => {
    this.setState({ isRetrying: true, retryCount: this.state.retryCount + 1 });

    // Add a small delay to show loading state
    setTimeout(() => {
      this.setState({ hasError: false, error: null, errorInfo: null, isRetrying: false });
      // Force a full page reload to reset Clerk state
      window.location.reload();
    }, 1000);
  };

  handleGoHome = () => {
    window.location.href = process.env.REACT_APP_LANDING_URL || 'https://uttik-landing-page-prod-325801373008.us-central1.run.app';
  };

  handleContactSupport = () => {
    const subject = `Authentication Error - ${this.state.errorType}`;
    const body = `Hello,\n\nI encountered an authentication error while trying to access Uttik.\n\nError Type: ${this.state.errorType}\nRetry Attempts: ${this.state.retryCount}\nTimestamp: ${new Date().toISOString()}\n\n${process.env.NODE_ENV === 'development' ? `Technical Details:\n${this.state.error?.message || 'No additional details'}` : ''}`;

    window.open(`mailto:support@uttik.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  render() {
    if (this.state.hasError) {
      const errorContent = this.getErrorContent();
      const { isRetrying, retryCount } = this.state;

      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center px-4">
          <div className="max-w-lg w-full">
            {/* Error Icon and Title */}
            <div className="text-center mb-8">
              <div className={`mx-auto w-20 h-20 ${errorContent.bgColor} rounded-full flex items-center justify-center mb-6 shadow-lg`}>
                {errorContent.icon}
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                {errorContent.title}
              </h1>
              <p className="text-gray-600 text-lg leading-relaxed mb-4">
                {errorContent.description}
              </p>
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm font-medium">
                💡 {errorContent.suggestion}
              </div>
            </div>

            {/* Retry Counter */}
            {retryCount > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <p className="text-gray-800 text-sm text-center">
                  Retry attempt #{retryCount}
                  {retryCount >= 3 && " - Consider contacting support if the issue persists"}
                </p>
              </div>
            )}

            {/* Error Details (development only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-2">
                  🔧 Developer Details (click to expand)
                </summary>
                <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono text-gray-800 overflow-auto max-h-32">
                  <div className="mb-2">
                    <strong>Error:</strong> {this.state.error.message}
                  </div>
                  <div className="mb-2">
                    <strong>Type:</strong> {this.state.errorType}
                  </div>
                  <div>
                    <strong>Stack:</strong> {this.state.error.stack}
                  </div>
                </div>
              </details>
            )}

            {/* Action Buttons */}
            <div className="space-y-4">
              <button
                onClick={this.handleRetry}
                disabled={isRetrying}
                className="w-full flex items-center justify-center px-6 py-4 bg-black text-white font-semibold rounded-xl hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isRetrying ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2" />
                    Try Again
                  </>
                )}
              </button>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={this.handleGoHome}
                  className="flex items-center justify-center px-4 py-3 bg-white text-gray-700 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Homepage
                </button>

                <button
                  onClick={this.handleContactSupport}
                  className="flex items-center justify-center px-4 py-3 bg-white text-gray-700 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Support
                </button>
              </div>
            </div>

            {/* Help Section */}
            <div className="mt-8 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">What can you do?</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Refresh the page or try again</li>
                <li>• Check your internet connection</li>
                <li>• Clear your browser cache</li>
                <li>• Contact support if the issue persists</li>
              </ul>
            </div>

            {/* Footer */}
            <div className="mt-6 text-center text-sm text-gray-500">
              <p>
                Error ID: {Date.now().toString(36)} •
                Time: {new Date().toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AuthErrorBoundary;